// PD-3985 — status cookie set by SocialBoostCreativeController@download_all_file
// (DOWNLOAD_ALL_STATUS_COOKIE). Timeout is comfortably above the server's own
// 600 s build limit so a slow-but-successful build is never reported as failed.
const DOWNLOAD_STATUS_COOKIE = 'creatives_download_status'
const DOWNLOAD_STATUS_POLL_MS = 500
const DOWNLOAD_STATUS_TIMEOUT_MS = 15 * 60 * 1000

export default {
  data() {
    return {
      isDownloading: false,
    }
  },
  beforeDestroy() {
    this.removeDownloadFrame()
  },
  methods: {
    isBM() {
      return (
        (this.$canAccess(['brand-admin', 'brand-employee', 'agency-client', 'agency', 'agency-employee']) &&
          !this.$is_PM &&
          !this.$is_GD) ||
        this.$isTopBrandAdmin()
      )
    },
    isTM() {
      return this.$canAccess(['traffic-manager']) && !this.$is_PM && !this.$is_GD
    },
    canSendToReview(creativeStatus) {
      const allowedFlightStatuses = ['in_progress']
      const allowedCreativeStatuses = ['new', 'revision']

      return (
        this.$SBCampaignStatusAccess?.(allowedFlightStatuses, this.flightStatus) &&
        this.$SBCampaignStatusAccess?.(allowedCreativeStatuses, creativeStatus) &&
        this.$is_GD
      )
    },
    canRequestRevisionOrApprove(creativeStatus) {
      const allowedCreativeStatuses = ['in_review']

      return this.$SBCampaignStatusAccess?.(allowedCreativeStatuses, creativeStatus) && this.isBM()
    },
    isInRequestRevision(creativeStatus) {
      const allowedCreativeStatuses = ['revision']

      return this.$SBCampaignStatusAccess?.(allowedCreativeStatuses, creativeStatus) && this.isBM()
    },
    canUploadCreative() {
      const allowedFlightStatuses = ['approved', 'in_progress']
      return this.$SBCampaignStatusAccess?.(allowedFlightStatuses, this.flightStatus) && this.$is_GD
    },
    canDeleteCreative(creativeStatus) {
      const allowedFlightStatuses = ['in_progress']
      const allowedCreativeStatuses = ['new']

      return (
        this.$SBCampaignStatusAccess?.(allowedFlightStatuses, this.flightStatus) &&
        this.$SBCampaignStatusAccess?.(allowedCreativeStatuses, creativeStatus) &&
        this.$is_GD
      )
    },
    canUploadReplaceAndDeleteFile(creativeStatus) {
      const allowedFlightStatuses = ['in_progress']
      const allowedCreativeStatuses = ['new', 'revision']

      return (
        this.$SBCampaignStatusAccess?.(allowedFlightStatuses, this.flightStatus) &&
        this.$SBCampaignStatusAccess?.(allowedCreativeStatuses, creativeStatus) &&
        this.$is_GD
      )
    },

    canDownload(creativeStatus) {
      const allowedCreativeStatuses = ['approved']

      return this.$SBCampaignStatusAccess?.(allowedCreativeStatuses, creativeStatus)
    },
    // Extracted from downloadCreativeFile so the bulk download (PD-3985) saves
    // the archive the same way. Behaviour is unchanged for the single download.
    saveBlobResponse(response, fallbackFilename) {
      const blob = new Blob([response.data], { type: response.headers['content-type'] })
      const url = URL.createObjectURL(blob)

      const disposition = response.headers['content-disposition']
      const match = disposition && disposition.match(/filename="?([^"]+)"?/)
      const filename = match?.[1] || fallbackFilename

      const link = document.createElement('a')
      link.href = url
      link.download = filename
      link.click()

      URL.revokeObjectURL(url)
    },
    async downloadCreativeFile(creative) {
      if (this.isDownloading) return

      this.isDownloading = true
      try {
        const response = await this.$store.dispatch('socialBoostCampaign/downloadCreative', {
          creative_id: creative.id,
        })

        this.saveBlobResponse(response, `creative_${creative.id}.zip`)
      } catch (error) {
        this.$message?.error?.('Download failed.')
        console.error('Download error:', error)
      } finally {
        this.isDownloading = false
      }
    },

    async downloadAllCreativeFiles() {
      if (this.isDownloading) return

      const flightId = Number(this.$route?.params?.flight_id)

      if (!flightId) return

      this.isDownloading = true
      try {
        // Two steps. Step one validates the flight (ownership, caps, temp-disk
        // headroom) and returns the archive URL plus a token; its errors arrive as
        // JSON and are shown by the axios interceptor. Step two loads that URL in a
        // hidden iframe: Content-Disposition: attachment streams the zip straight
        // to disk, so it is never held in tab memory, and a JSON error body renders
        // inside the iframe instead of being saved as a corrupt .zip. A navigation
        // cannot report back, so the server sets a status cookie carrying the token
        // on every outcome, and the loading state holds until it arrives.
        const { data } = await this.$store.dispatch('socialBoostCampaign/downloadAllCreatives', {
          flight_id: flightId,
        })

        const status = await this.awaitDownloadStatus(data.token, (onFrameResponse) => this.startFrameDownload(data.url, onFrameResponse))

        if (status.status !== 'ok') {
          this.$message?.error?.(status.message || 'Download failed. Please try again.')
        }
      } catch (error) {
        // A 422 with a message ("no creatives available", "too large") is already
        // shown by the axios error interceptor; only cover the silent failures.
        if (!error?.response?.data?.message) {
          this.$message?.error?.('Download failed.')
        }
        console.error('Download all error:', error)
      } finally {
        this.isDownloading = false
      }
    },
    // Loads the archive URL in a hidden iframe so the download never leaves the
    // dashboard and never touches tab memory. The frame stays attached until the
    // next download or the component is destroyed — removing it as soon as the
    // status cookie arrives could cancel a download that is still streaming.
    //
    // Only the controller sets the status cookie. Anything that answers before it
    // runs — the per-user throttle (429), the ownership check (403), an expired
    // session (401), a binding 404, a 500 outside its try — sends no cookie but does
    // render a body inside this same-origin frame. The load listener reads that body
    // and reports it, so those failures surface at once instead of spinning until
    // the timeout. An attachment response leaves the frame's document empty (or
    // fires no load at all), which is the signal to keep waiting for the cookie.
    startFrameDownload(url, onResponse) {
      this.removeDownloadFrame()

      const frame = document.createElement('iframe')
      frame.setAttribute('aria-hidden', 'true')
      frame.style.display = 'none'
      frame.addEventListener('load', () => {
        const body = this.readFrameResponseBody(frame)

        if (body !== '') {
          onResponse({ status: 'error', message: this.frameResponseMessage(body) })
        }
      })
      frame.src = url
      document.body.appendChild(frame)
      this.$_downloadFrame = frame
    },
    // Text of the document the frame ended up showing, '' for a blank frame or one
    // whose document cannot be read.
    readFrameResponseBody(frame) {
      try {
        return (frame.contentDocument?.body?.textContent || '').trim()
      } catch (error) {
        return ''
      }
    },
    // A JSON error body carries the server's message; the throttle's "Too Many
    // Attempts" gets a hint that waiting is what helps; anything else (an HTML
    // error page, say) falls back to a generic message.
    frameResponseMessage(body) {
      let message = ''

      try {
        message = JSON.parse(body)?.message || ''
      } catch (error) {
        message = ''
      }

      if (/too many/i.test(message || body)) {
        return 'Too many downloads in a row. Please wait a minute and try again.'
      }

      return message || 'Download failed. Please try again.'
    },
    removeDownloadFrame() {
      if (this.$_downloadFrame?.parentNode) {
        this.$_downloadFrame.parentNode.removeChild(this.$_downloadFrame)
      }
      this.$_downloadFrame = null
    },
    // Resolves with the first of: the { token, status, message } the controller put
    // in the status cookie for this token; an error the frame rendered instead of
    // an attachment (see startFrameDownload); or a timeout, which is now only the
    // backstop for a proxy or worker that never answers. The cookie is cleared
    // before starting so one left over from an earlier download cannot satisfy
    // this one.
    awaitDownloadStatus(token, start) {
      return new Promise((resolve) => {
        this.clearDownloadStatusCookie()

        let settled = false
        let timer = null
        const settle = (status) => {
          if (settled) return
          settled = true
          clearInterval(timer)
          this.clearDownloadStatusCookie()
          resolve({ token, ...status })
        }

        start((frameResponse) => settle(frameResponse))

        const startedAt = Date.now()
        timer = setInterval(() => {
          const status = this.readDownloadStatusCookie()

          if (status && status.token === token) {
            settle(status)
            return
          }

          if (Date.now() - startedAt > DOWNLOAD_STATUS_TIMEOUT_MS) {
            settle({ status: 'error', message: 'The download took too long. Please try again.' })
          }
        }, DOWNLOAD_STATUS_POLL_MS)
      })
    },
    readDownloadStatusCookie() {
      const prefix = `${DOWNLOAD_STATUS_COOKIE}=`
      const raw = document.cookie
        .split(';')
        .map((part) => part.trim())
        .find((part) => part.startsWith(prefix))

      if (!raw) return null

      try {
        return JSON.parse(decodeURIComponent(raw.slice(prefix.length)))
      } catch (error) {
        return null
      }
    },
    clearDownloadStatusCookie() {
      document.cookie = `${DOWNLOAD_STATUS_COOKIE}=; Max-Age=0; path=/`
    },
  },
}
